import { Mail, Sparkles, X } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const getStoredValue = (key: string) => {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setStoredValue = (key: string, value: string) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Swallow write errors (private mode, quota exceeded, etc.)
  }
};

export function StickySubscribeForm() {
  const [isOpen, setIsOpen] = useState(() => {
    // Check if user has previously dismissed the form
    return getStoredValue("newsletter_dismissed") !== "true";
  });
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [hasSubscribed, setHasSubscribed] = useState(() => {
    return getStoredValue("newsletter_subscribed") === "true";
  });

  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling past 600px (approximately past the first section)
      const scrollThreshold = 600;
      if (window.scrollY > scrollThreshold && !hasSubscribed) {
        setIsVisible(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial scroll position

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hasSubscribed]);

  const handleDismiss = () => {
    setIsOpen(false);
    setStoredValue("newsletter_dismissed", "true");
  };

  if (hasSubscribed || !isOpen || !isVisible) {
    return null;
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setIsLoading(true);

    // Wrap async logic in an immediately invoked async function
    void (async () => {
      try {
        const response = await fetch("/newsletter_subscriptions", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "",
          },
          body: JSON.stringify({
            newsletter_subscription: { email },
          }),
        });

        const data = (await response.json()) as {
          message?: string;
          errors?: string[];
        };

        if (response.ok) {
          toast.success(data.message ?? "Thanks for subscribing!");
          setStoredValue("newsletter_subscribed", "true");
          setHasSubscribed(true);
        } else {
          toast.error(data.message ?? data.errors?.join(", ") ?? "Something went wrong");
        }
      } catch {
        toast.error("Failed to subscribe. Please try again.");
      } finally {
        setIsLoading(false);
      }
    })();
  };

  return (
    <div className="animate-in slide-in-from-bottom-5 fixed right-0 bottom-0 left-0 z-50">
      <div className="mx-auto max-w-7xl px-4 pb-4 sm:px-6 sm:pb-6">
        <div className="border-border bg-background relative overflow-hidden rounded-lg border shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 via-red-500/10 to-orange-500/10" />

          <button
            onClick={handleDismiss}
            className="text-muted-foreground hover:text-foreground absolute top-2 right-2 z-10 rounded-md p-2 transition-colors"
            type="button"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="relative px-4 py-5 sm:p-6">
            <div className="sm:flex sm:items-center sm:justify-between">
              <div className="text-center sm:text-left">
                <div className="mb-2 flex items-center justify-center gap-2 sm:justify-start">
                  <Sparkles className="h-5 w-5 text-orange-500" />
                  <h3 className="text-foreground text-lg leading-6 font-semibold">Get updates about OG Pilot</h3>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                className="mt-4 sm:mx-6 sm:mt-0 sm:flex sm:flex-shrink-0 sm:items-center"
                noValidate
              >
                <div className="relative">
                  <Mail className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full pr-3 pl-10 sm:w-64"
                    disabled={isLoading}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="mt-3 w-full bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 sm:mt-0 sm:ml-3 sm:w-auto"
                >
                  {isLoading ? "Subscribing..." : "Subscribe"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
